import { useEffect, useState } from 'react';
import { useAccount } from '../account/context';
import { getAccountScanRuns, getScanRunDetail, getRunsComparison } from '../account';
import ScanHistory from './ScanHistory';
import SchedulesSection from './SchedulesSection';
import QuerySets from './QuerySets';
import EyePluginCard from './EyePluginCard';
import ShareLinks from './ShareLinks';
import DashboardShell, { PageHead, type DashboardPage } from './DashboardShell';
import App from '../App';
import RecentScans from './RecentScans';
import About from './About';
import Faq from './Faq';

/**
 * The signed-in account, as a dashboard rather than one long column.
 *
 * It used to stack every panel on top of the next: question sets, schedules,
 * the plugin card, share links, then the history and its charts. Five unrelated
 * jobs in one scroll, with the thing most people came for at the bottom.
 *
 * Now each is a page behind the rail, and the one people came for is first.
 * **No panel was rewritten.** They are the same components in a different
 * arrangement, which is deliberate: the shell is the change, and if this work
 * starts editing the panels then the scope has quietly grown.
 */

/**
 * Everything the tool is, reachable from one place.
 *
 * The first version of this shell carried only the account panels, which left a
 * signed-in client with no way to scan, read the explainer or open the FAQ
 * without leaving the dashboard for the marketing page. Those are not marketing;
 * they are the tool. They are pages here now.
 */
const PAGES: DashboardPage[] = [
  {
    id: 'scan',
    label: 'Run a scan',
    blurb: 'Enter a public URL and see whether AI can reach it, read it and cite it.',
    icon: '⌕',
    group: 'tool',
    ownerOnly: true,
  },
  {
    id: 'history',
    label: 'Scans & progress',
    blurb: 'Every run, what moved, and who AI names instead of you.',
    icon: '◳',
    group: 'tool',
  },
  {
    id: 'recent',
    label: 'Recent scans',
    blurb: 'The last runs across your sites, newest first.',
    icon: '≡',
    group: 'tool',
  },
  {
    id: 'questions',
    label: 'Question sets',
    blurb: 'The questions AI is asked about each of your sites. A trend only means something across runs that asked the same ones.',
    icon: '?',
    group: 'setup',
    ownerOnly: true,
  },
  {
    id: 'schedules',
    label: 'Automatic re-scans',
    blurb: 'Re-run an approved set on a schedule and get an email with what changed.',
    icon: '◷',
    group: 'setup',
    ownerOnly: true,
  },
  {
    id: 'share',
    label: 'Shared views',
    blurb: 'Read-only links into this account, for a client or an agency.',
    icon: '⇢',
    group: 'setup',
    ownerOnly: true,
  },
  {
    id: 'plugin',
    label: 'WordPress plugin',
    blurb: 'AI EYE shows these results inside your own wp-admin.',
    icon: '⬡',
    group: 'setup',
    ownerOnly: true,
  },
  {
    id: 'how',
    label: 'How AI citation works',
    blurb: 'What actually decides whether AI names you, what you can change, and what no amount of on-page work will fix.',
    icon: '◎',
    group: 'learn',
  },
  {
    id: 'faq',
    label: 'FAQ',
    blurb: 'What this measures, what it costs, and what happens to your data.',
    icon: '¶',
    group: 'learn',
  },
];

/**
 * Which page the URL is asking for. One level deep on purpose, no router.
 *
 * - `#account/<name>` is that page
 * - `#account` is the history, which is what that link has always meant
 * - **anything else, including no hash at all, is the scan**
 *
 * That last line is what makes a signed-in client landing on
 * `/ai-visibility-scan` get the scan inside the dashboard rather than an
 * account page they did not ask for.
 */
function pageFromHash(): string {
  const h = window.location.hash;
  const m = /^#account\/([a-z-]+)/.exec(h);
  if (m) return m[1]!;
  return h.startsWith('#account') ? 'history' : 'scan';
}

export default function ProfilePage(): JSX.Element {
  const { account, setModal } = useAccount();
  // Domains this account has scanned — a set can be written for any of them,
  // including ones that have no set yet.
  const [domains, setDomains] = useState<string[]>([]);
  const [page, setPage] = useState(pageFromHash);

  useEffect(() => {
    if (account) void getAccountScanRuns({ page: 1 }).then((d) => setDomains(d.domains)).catch(() => setDomains([]));
  }, [account]);

  // Back and forward have to work. A dashboard that eats the back button is one
  // people leave by closing the tab. `popstate` as well as `hashchange`,
  // because clearing the hash for the scan page uses pushState, which fires
  // only the first of those.
  useEffect(() => {
    const onNav = (): void => setPage(pageFromHash());
    window.addEventListener('hashchange', onNav);
    window.addEventListener('popstate', onNav);
    return () => {
      window.removeEventListener('hashchange', onNav);
      window.removeEventListener('popstate', onNav);
    };
  }, []);

  // A share-link session. Named once here rather than repeating the comparison
  // at every place that hides something.
  const viewer = account?.kind === 'viewer';

  if (!account) {
    return (
      <main className="max-w-2xl mx-auto px-6 py-16 text-center">
        <h1 className="font-serif text-3xl mb-3">My scans &amp; progress</h1>
        <p className="text-text-secondary mb-5">Sign in to see your scan history and progress over time.</p>
        <button onClick={() => setModal('signin')} className="bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2.5 text-sm hover:opacity-90 transition">Sign in</button>
        <div className="mt-6"><a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = ''; }} className="font-mono text-sm text-text-secondary hover:text-cyan">← back to scan</a></div>
      </main>
    );
  }

  const plan: 'free' | 'pro' = account.subscription.tier === 'pro' ? 'pro' : 'free';
  // A viewer landing on a page they cannot see would get an empty screen, so
  // anything owner-only falls back to the history they came to read.
  const allowed = PAGES.filter((p) => !(viewer && p.ownerOnly)).map((p) => p.id);
  const current = allowed.includes(page) ? page : 'history';
  const meta = PAGES.find((p) => p.id === current)!;

  const navigate = (id: string): void => {
    /*
      The scan is the root, so it clears the hash rather than adding one.
      Otherwise the same screen would live at two addresses, `/` and
      `#account/scan`, and a client who bookmarked one would find the other.
    */
    if (id === 'scan') {
      history.pushState(null, '', window.location.pathname + window.location.search);
      setPage('scan');
      return;
    }
    window.location.hash = id === 'history' ? 'account' : `account/${id}`;
    setPage(id);
  };

  return (
    <DashboardShell
      pages={PAGES}
      current={current}
      onNavigate={navigate}
      account={{ email: account.email, tier: plan }}
      viewer={viewer}
    >
      {/*
        A viewer is told what they are looking at, once, at the top of whatever
        page they land on. Without it, somebody sent a link finds buttons
        missing and assumes the tool is broken.
      */}
      {viewer && (
        <div className="border border-cyan/40 rounded-xl px-4 py-3 mb-5 bg-cyan/5">
          <p className="text-sm text-text-primary">
            You are looking at a shared, read-only view of {account.email}&apos;s account.
          </p>
          <p className="text-sm text-text-secondary mt-1">
            Everything here is visible to you and you can export it. Scanning, changes and anything that
            costs money stay with the account owner. Ask them if you need a fresh scan.
          </p>
        </div>
      )}

      {/* The scan page carries its own heading and call to action, so a page
          head above it would be the third title on one screen. */}
      {current !== 'scan' && (
      <PageHead
        title={meta.label}
        blurb={meta.blurb}
        /* Stays inside the dashboard: linking out to the marketing page would
           drop somebody out of the shell they are working in. */
        action={current === 'history' && !viewer ? (
          <button
            onClick={() => navigate('scan')}
            className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2.5 text-sm hover:opacity-90 transition"
          >Run a scan</button>
        ) : undefined}
      />
      )}

      {current === 'history' && (
        <ScanHistory
          fetcher={getAccountScanRuns}
          detailFetcher={getScanRunDetail}
          comparisonFetcher={getRunsComparison}
          plan={plan}
          viewer={{ email: account.email }}
          readOnly={viewer}
        />
      )}

      {/*
        The scan page itself, inside the shell.

        `App` is the whole scan surface and it is not rewritten here: it is
        rendered as a page, with its own right-hand rail suppressed because the
        dashboard already has one on the left and two rails is one too many.
      */}
      {current === 'scan' && <App embedded />}

      {current === 'recent' && (
        <RecentScans
          embedded
          onProgress={() => navigate('history')}
          onScan={() => navigate('scan')}
          onAbout={() => navigate('how')}
          onFaq={() => navigate('faq')}
        />
      )}

      {current === 'questions' && <QuerySets domains={domains} plan={plan} />}
      {current === 'schedules' && <SchedulesSection plan={plan} email={account.email} />}
      {current === 'share' && <ShareLinks domains={domains} />}
      {current === 'plugin' && <EyePluginCard plan={plan} />}
      {current === 'how' && <About embedded />}
      {current === 'faq' && <Faq embedded />}
    </DashboardShell>
  );
}
